import axios, { AxiosResponse, AxiosError } from 'axios';
import { config } from '@/config';
import { AuthResponse, ApiResponse } from '@/types';
import toast from 'react-hot-toast';

// Create axios instance
const api = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 60000, // 60 seconds for blockchain operations
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token storage
const TOKEN_KEY = 'work-tracker-token';

export const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

export const setToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    // Don't show toast for certain expected errors to reduce noise
    const url = error.config?.url || '';
    const isExpectedError = (
      url.includes('/rewards/admin/failed') ||
      url.includes('/rewards/admin/pending') ||
      (error.response?.status === 404 && url.includes('/admin/'))
    );

    if (error.response?.status === 401) {
      // Token expired or invalid
      removeToken();
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
        if (!isExpectedError) {
          toast.error('Session expired. Please login again.');
        }
      }
    } else if (error.response?.status === 403) {
      if (!isExpectedError) {
        toast.error('Access denied. Insufficient permissions.');
      }
    } else if (error.response?.status === 429) {
      const retryAfter = (error.response?.data as any)?.retryAfter;
      const message = retryAfter
        ? `Too many requests. Please wait ${retryAfter} seconds.`
        : 'Too many requests. Please slow down.';
      toast.error(message);
    } else if (error.response?.status && error.response.status >= 500) {
      if (!isExpectedError) {
        toast.error('Server error. Please try again later.');
      }
    } else if (error.code === 'ECONNABORTED') {
      if (!isExpectedError) {
        toast.error('Request timeout. Please check your connection.');
      }
    } else if (!error.response) {
      if (!isExpectedError) {
        toast.error('Network error. Please check your connection.');
      }
    }

    return Promise.reject(error);
  }
);

// Generic API request function
const makeRequest = async <T>(
  method: 'get' | 'post' | 'put' | 'delete',
  url: string,
  data?: any
): Promise<T> => {
  try {
    const response = await api.request({
      method,
      url,
      data,
    });
    
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data) {
      throw error.response.data;
    }
    throw error;
  }
};

// API methods
export const apiClient = {
  // Generic methods
  get: <T = any>(url: string) => makeRequest<T>('get', url),
  post: <T = any>(url: string, data?: any) => makeRequest<T>('post', url, data),
  put: <T = any>(url: string, data?: any) => makeRequest<T>('put', url, data),
  delete: <T = any>(url: string) => makeRequest<T>('delete', url),

  // Auth methods
  auth: {
    login: (data: any) => makeRequest<AuthResponse>('post', '/auth/login', data),
    register: (data: any) => makeRequest<AuthResponse>('post', '/auth/register', data),
    verify: (token: string) => makeRequest<AuthResponse>('post', '/auth/verify', { token }),
  },

  // User methods
  users: {
    getProfile: () => makeRequest<ApiResponse>('get', '/users/profile'),
    updateProfile: (data: any) => makeRequest<ApiResponse>('put', '/users/profile', data),
    getDashboard: () => makeRequest<ApiResponse>('get', '/users/dashboard'),
    getWorkSessions: (params?: any) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return makeRequest<ApiResponse>('get', `/users/work-sessions${queryString}`);
    },
    getRewards: (params?: any) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return makeRequest<ApiResponse>('get', `/users/rewards${queryString}`);
    },
    getBalance: () => makeRequest<ApiResponse>('get', '/users/balance'),
    // Admin methods
    getAllUsers: (params?: any) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return makeRequest<ApiResponse>('get', `/users/admin/users${queryString}`);
    },
    deactivateUser: (id: string) => makeRequest<ApiResponse>('put', `/users/admin/users/${id}/deactivate`),
    activateUser: (id: string) => makeRequest<ApiResponse>('put', `/users/admin/users/${id}/activate`),
  },

  // Work session methods
  workSessions: {
    create: (data: any) => makeRequest<ApiResponse>('post', '/work-sessions', data),
    get: (id: string) => makeRequest<ApiResponse>('get', `/work-sessions/${id}`),
    update: (id: string, data: any) => makeRequest<ApiResponse>('put', `/work-sessions/${id}`, data),
    delete: (id: string) => makeRequest<ApiResponse>('delete', `/work-sessions/${id}`),
    // Admin methods
    getAll: (params?: any) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return makeRequest<ApiResponse>('get', `/work-sessions/admin/sessions${queryString}`);
    },
    approve: (id: string) => makeRequest<ApiResponse>('put', `/work-sessions/admin/sessions/${id}/approve`),
    getPending: (params?: any) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return makeRequest<ApiResponse>('get', `/work-sessions/admin/pending${queryString}`);
    },
  },

  // Reward methods
  rewards: {
    getMine: (params?: any) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return makeRequest<ApiResponse>('get', `/rewards/mine${queryString}`);
    },
    // Get confirmation status for a reward (polling endpoint)
    getStatus: (rewardId: string) => makeRequest<ApiResponse>('get', `/rewards/${rewardId}/status`),
    // Admin methods
    getPending: () => makeRequest<ApiResponse>('get', '/rewards/admin/pending'),
    getFailed: () => makeRequest<ApiResponse>('get', '/rewards/admin/failed'),
    retry: (id: string) => makeRequest<ApiResponse>('post', `/rewards/admin/retry/${id}`),
    getWorkerStatus: () => makeRequest<ApiResponse>('get', '/rewards/admin/worker-status'),
    getTransactions: (params?: { status?: string; limit?: number; offset?: number }) => {
      const queryString = params ? '?' + new URLSearchParams(
        Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
      ).toString() : '';
      return makeRequest<ApiResponse>('get', `/rewards/admin/transactions${queryString}`);
    },
    getNonceStatus: () => makeRequest<ApiResponse>('get', '/rewards/admin/nonce-status'),
    resetNonce: () => makeRequest<ApiResponse>('post', '/rewards/admin/nonce-reset'),
  },

  // Payout request methods
  payoutRequests: {
    // User endpoints
    getEligible: () => makeRequest<ApiResponse>('get', '/payout-requests/eligible'),
    create: (data: { sessionIds?: string[]; selectAll?: boolean }) =>
      makeRequest<ApiResponse>('post', '/payout-requests', data),
    getMine: (params?: any) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return makeRequest<ApiResponse>('get', `/payout-requests${queryString}`);
    },
    get: (id: string) => makeRequest<ApiResponse>('get', `/payout-requests/${id}`),
    // Admin endpoints
    getPending: (params?: any) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return makeRequest<ApiResponse>('get', `/payout-requests/admin/pending${queryString}`);
    },
    getHistory: (params?: any) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return makeRequest<ApiResponse>('get', `/payout-requests/admin/history${queryString}`);
    },
    approve: (id: string) => makeRequest<ApiResponse>('put', `/payout-requests/admin/${id}/approve`),
    reject: (id: string, reason: string) =>
      makeRequest<ApiResponse>('put', `/payout-requests/admin/${id}/reject`, { reason }),
    bulkApprove: (requestIds: string[]) =>
      makeRequest<ApiResponse>('put', '/payout-requests/admin/bulk-approve', { requestIds }),
  },

  // Settings methods (admin only)
  settings: {
    get: () => makeRequest<ApiResponse>('get', '/settings'),
    update: (data: any) => makeRequest<ApiResponse>('put', '/settings', data),
    toggleEmergencyMode: (enabled: boolean) =>
      makeRequest<ApiResponse>('post', '/settings/emergency-mode', { enabled }),
    getPendingSignatures: () => makeRequest<ApiResponse>('get', '/settings/signature-requests'),
    setUserOverride: (userId: string, override: string) =>
      makeRequest<ApiResponse>('put', `/settings/users/${userId}/override`, { override }),
    pauseContract: () => makeRequest<ApiResponse>('post', '/settings/emergency-pause-contract'),
    getContractStatus: () => makeRequest<ApiResponse>('get', '/settings/contract-status'),
    getMinterUsage: () => makeRequest<ApiResponse>('get', '/settings/minter-usage'),
    getAutoApprovalStatus: () => makeRequest<ApiResponse>('get', '/settings/auto-approval-status'),
    getPayoutStats: () => makeRequest<ApiResponse>('get', '/settings/payout-stats'),
  },

  // Monitoring methods (admin only)
  monitoring: {
    getHybridPayoutMetrics: () =>
      makeRequest<ApiResponse>('get', '/monitoring/hybrid-payout'),

    getMetricsHistory: (hours: number = 24) =>
      makeRequest<ApiResponse>('get', `/monitoring/metrics/history?hours=${hours}`),

    getAlerts: (limit: number = 50) =>
      makeRequest<ApiResponse>('get', `/monitoring/alerts?limit=${limit}`),

    dismissAlert: (alertId: string, resolution?: string) =>
      makeRequest<ApiResponse>('post', `/monitoring/alerts/${alertId}/resolve`, { resolution }),

    dismissAlertsBatch: (alertIds: string[], resolution?: string) =>
      makeRequest<ApiResponse>('post', '/monitoring/alerts/dismiss-batch', { alertIds, resolution }),

    getAlertConfig: () =>
      makeRequest<ApiResponse>('get', '/monitoring/alert-config'),

    updateAlertConfig: (config: any) =>
      makeRequest<ApiResponse>('put', '/monitoring/alert-config', config),
  },

  // Health check
  health: () => makeRequest<ApiResponse>('get', '/health'),
};

export default apiClient;